import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, Download, Loader2, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminResultsPage = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadResults();
  }, []);

  const loadResults = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/competition/leaderboard`, { withCredentials: true });
      setLeaderboard(res.data.leaderboard || []);
    } catch (e) {
      setError('No results available yet');
    } finally {
      setLoading(false);
    }
  };

  const exportResults = async (format) => {
    setExporting(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/admin/export-results?format=${format}`, { withCredentials: true });
      if (format === 'csv') {
        const blob = new Blob([res.data.data], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = res.data.filename || 'results.csv';
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'olympiad_results.json';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to export');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          Results
        </h1>
        <p className="text-sm text-[#64748b] mt-1">Live leaderboard and post-event results.</p>
      </div>

      {/* Export Buttons */}
      <div className="flex gap-3 mb-6">
        <button
          onClick={() => exportResults('csv')}
          disabled={exporting}
          className="px-5 py-2.5 rounded-xl font-bold bg-[#2565eb] text-white text-sm flex items-center gap-2 hover:bg-[#1e3a8a] transition-all disabled:opacity-50"
          data-testid="export-csv"
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
          Export CSV
        </button>
        <button
          onClick={() => exportResults('json')}
          disabled={exporting}
          className="px-5 py-2.5 rounded-xl font-bold border-2 border-[#2565eb] text-[#2565eb] text-sm flex items-center gap-2 hover:bg-[#eff6ff] transition-all disabled:opacity-50"
          data-testid="export-json"
        >
          <Download className="w-4 h-4" />
          Export JSON
        </button>
      </div>

      {error && (
        <div className="bg-[#fef2f2] border-2 border-[#fecaca] text-[#dc2626] px-4 py-3 rounded-2xl mb-4 text-sm font-medium">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#2565eb] animate-spin" />
        </div>
      ) : leaderboard.length === 0 ? (
        <div className="card-olympiad p-12 text-center">
          <Trophy className="w-16 h-16 text-[#94a3b8] mx-auto mb-4" />
          <p className="text-lg font-semibold text-[#64748b]">No results yet</p>
          <p className="text-sm text-[#94a3b8]">Results will appear once the competition has started.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Podium (Top 3) */}
          {leaderboard.length >= 3 && (
            <div className="grid grid-cols-3 gap-4 mb-6">
              {[1, 0, 2].map((idx) => {
                const p = leaderboard[idx];
                if (!p) return null;
                const colors = ['bg-[#face39]', 'bg-[#94a3b8]', 'bg-[#cd7f32]'];
                const heights = ['h-32', 'h-24', 'h-20'];
                return (
                  <div key={idx} className="flex flex-col items-center">
                    <div className={`w-12 h-12 ${colors[idx]} rounded-full flex items-center justify-center text-white font-bold text-lg mb-2`}>
                      {idx + 1}
                    </div>
                    <p className="text-sm font-bold text-[#0f172a] text-center">{p.display_name}</p>
                    <p className="text-xs text-[#64748b]">{p.total_score} pts</p>
                    <div className={`w-full ${heights[idx]} ${colors[idx]} rounded-t-xl mt-2 opacity-30`} />
                  </div>
                );
              })}
            </div>
          )}

          {/* Full Leaderboard Table */}
          <div className="card-olympiad overflow-hidden">
            <table className="w-full" data-testid="results-table">
              <thead>
                <tr className="bg-[#f8fafc] border-b-2 border-[#e2e8f0]">
                  <th className="px-4 py-3 text-left text-xs font-bold text-[#64748b] uppercase">Rank</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-[#64748b] uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-[#64748b] uppercase">Score</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-[#64748b] uppercase">Rounds</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {leaderboard.map((p, i) => (
                  <tr key={i} className={`${i < 3 ? 'bg-[#fffbeb]' : ''} hover:bg-[#f8fafc] transition-colors`}>
                    <td className="px-4 py-3 text-sm font-bold text-[#0f172a]">#{p.rank || i + 1}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-[#0f172a]">{p.display_name}</td>
                    <td className="px-4 py-3 text-sm font-bold text-[#2565eb]">{p.total_score}</td>
                    <td className="px-4 py-3 text-xs text-[#64748b]">
                      {p.round_scores ? Object.values(p.round_scores).join(', ') : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminResultsPage;
